/**
 * Activity Trigger Config Panel
 *
 * Modal panel for configuring activity-based assessment triggers for a camera.
 * Allows setting up motion detection rules, capture parameters, and rate limiting.
 */

import { useState, useEffect } from 'react';
import {
  X,
  Zap,
  Clock,
  Calendar,
  AlertTriangle,
  Loader2,
  Settings2,
  Timer,
  RotateCcw,
  Activity,
  Info,
} from 'lucide-react';
import {
  useActivityTriggerConfig,
  useUpdateActivityTriggerConfig,
  formatCooldownRemaining,
  formatActiveDays,
  type UpdateActivityTriggerConfig,
} from '../../hooks/useActivityTriggers';

interface ActivityTriggerConfigPanelProps {
  cameraId: string;
  cameraName: string;
  isOpen: boolean;
  onClose: () => void;
}

// Day of week options
const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

// Hour options for dropdowns
const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i === 0 ? '12:00 AM' : i === 12 ? '12:00 PM' : i < 12 ? `${i}:00 AM` : `${i - 12}:00 PM`,
}));

export function ActivityTriggerConfigPanel({
  cameraId,
  cameraName,
  isOpen,
  onClose,
}: ActivityTriggerConfigPanelProps) {
  // Queries
  const { data: config, isLoading } = useActivityTriggerConfig(cameraId);

  // Mutations
  const updateMutation = useUpdateActivityTriggerConfig();

  // Local form state
  const [formData, setFormData] = useState<UpdateActivityTriggerConfig>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Auto-detect browser timezone
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Initialize form data when config loads
  useEffect(() => {
    if (config) {
      setFormData({
        isEnabled: config.isEnabled,
        altaRuleNames: config.altaRuleNames,
        captureDurationMinutes: config.captureDurationMinutes,
        captureOffsetSeconds: config.captureOffsetSeconds,
        cooldownMinutes: config.cooldownMinutes,
        maxTriggersPerShift: config.maxTriggersPerShift,
        activeStartHour: config.activeStartHour,
        activeEndHour: config.activeEndHour,
        activeDaysOfWeek: config.activeDaysOfWeek,
        timezone: config.timezone ?? browserTimezone,
      });
      setHasChanges(false);
    }
  }, [config]);

  // Update form field
  const updateField = <K extends keyof UpdateActivityTriggerConfig>(
    field: K,
    value: UpdateActivityTriggerConfig[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Handle save
  const handleSave = async () => {
    await updateMutation.mutateAsync({ cameraId, data: formData });
    setHasChanges(false);
  };

  // Parse selected days
  const selectedDays = (formData.activeDaysOfWeek ?? '1,2,3,4,5')
    .split(',')
    .map((d) => parseInt(d.trim(), 10));

  // Toggle day selection
  const toggleDay = (day: number) => {
    const current = new Set(selectedDays);
    if (current.has(day)) {
      current.delete(day);
    } else {
      current.add(day);
    }
    const sorted = Array.from(current).sort((a, b) => a - b);
    updateField('activeDaysOfWeek', sorted.join(','));
  };

  // Handle rule names input (comma-separated)
  const handleRuleNamesChange = (value: string) => {
    const rules = value
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
    updateField('altaRuleNames', rules);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl m-4">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
              <Zap size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Activity Triggers
              </h2>
              <p className="text-sm text-gray-500">{cameraName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 size={32} className="text-guild-green animate-spin" />
            <p className="mt-4 text-sm text-gray-500">Loading configuration...</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Activity size={20} className={formData.isEnabled ? 'text-guild-green' : 'text-gray-400'} />
                <div>
                  <h3 className="font-medium text-gray-900">Enable Activity Triggers</h3>
                  <p className="text-sm text-gray-500">
                    Automatically trigger assessments when motion rules fire
                  </p>
                </div>
              </div>
              <button
                onClick={() => updateField('isEnabled', !formData.isEnabled)}
                className={`
                  relative w-12 h-7 rounded-full transition-colors duration-200
                  ${formData.isEnabled ? 'bg-guild-green' : 'bg-gray-300'}
                `}
              >
                <div
                  className={`
                    absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow
                    transition-transform duration-200
                    ${formData.isEnabled ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            {/* Stats Bar */}
            {config?.stats && (
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {config.stats.triggersToday}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Triggers Today
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {config.stats.triggersRemaining}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Remaining
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <div className="text-sm font-medium text-gray-900">
                    {config.stats.cooldownRemaining > 0
                      ? formatCooldownRemaining(config.stats.cooldownRemaining)
                      : 'Ready'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Cooldown
                  </div>
                </div>
              </div>
            )}

            {/* Configuration Sections */}
            <div className="space-y-6">
              {/* Alta Rule Names */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Settings2 size={16} />
                  Trigger Rule Names
                </label>
                <input
                  type="text"
                  value={(formData.altaRuleNames ?? []).join(', ')}
                  onChange={(e) => handleRuleNamesChange(e.target.value)}
                  placeholder="e.g., Motion Detected, Line Crossing"
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green"
                />
                <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1">
                  <Info size={12} />
                  Comma-separated list of rule names that trigger assessments
                </p>
              </div>

              {/* Capture Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Timer size={16} />
                    Capture Duration
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={30}
                      value={formData.captureDurationMinutes ?? 5}
                      onChange={(e) => updateField('captureDurationMinutes', parseInt(e.target.value, 10))}
                      className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      min
                    </span>
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <RotateCcw size={16} />
                    Capture Offset
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={-300}
                      max={0}
                      value={formData.captureOffsetSeconds ?? -30}
                      onChange={(e) => updateField('captureOffsetSeconds', parseInt(e.target.value, 10))}
                      className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      sec
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Negative = before event
                  </p>
                </div>
              </div>

              {/* Rate Limiting */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock size={16} />
                    Cooldown Period
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={15}
                      max={240}
                      value={formData.cooldownMinutes ?? 60}
                      onChange={(e) => updateField('cooldownMinutes', parseInt(e.target.value, 10))}
                      className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      min
                    </span>
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <AlertTriangle size={16} />
                    Max Triggers/Shift
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={formData.maxTriggersPerShift ?? 5}
                    onChange={(e) => updateField('maxTriggersPerShift', parseInt(e.target.value, 10))}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green"
                  />
                </div>
              </div>

              {/* Active Time Window */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Calendar size={16} />
                  Active Time Window
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Start Hour</label>
                    <select
                      value={formData.activeStartHour ?? 6}
                      onChange={(e) => updateField('activeStartHour', parseInt(e.target.value, 10))}
                      className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green"
                    >
                      {HOURS.map((h) => (
                        <option key={h.value} value={h.value}>{h.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">End Hour</label>
                    <select
                      value={formData.activeEndHour ?? 22}
                      onChange={(e) => updateField('activeEndHour', parseInt(e.target.value, 10))}
                      className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green"
                    >
                      {HOURS.map((h) => (
                        <option key={h.value} value={h.value}>{h.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Active Days */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Active Days
                </label>
                <div className="flex gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day.value}
                      onClick={() => toggleDay(day.value)}
                      className={`
                        flex-1 py-2 rounded-xl text-sm font-medium transition-colors
                        ${selectedDays.includes(day.value)
                          ? 'bg-guild-green text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                      `}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Currently: {formatActiveDays(formData.activeDaysOfWeek ?? '1,2,3,4,5')}
                </p>
              </div>
            </div>

          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className="
              inline-flex items-center gap-2 px-5 py-2.5
              bg-guild-green text-white text-sm font-semibold rounded-xl
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
              disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none
            "
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
